import type { Track, WaypointAttributes } from './types/scenario';
import { type ClassificationMethod, computeBreaks } from './classification';

// --- Color Mode Types ---

/** Built-in color modes with fixed coloring logic. */
export type BuiltInColorMode =
  | 'track'
  | 'sog'
  | 'cog'
  | 'nav_status'
  | 'name'
  | 'mmsi';

/**
 * Color mode for track segments.
 * - Built-in modes: 'track', 'sog', 'cog', 'nav_status', 'name', 'mmsi'
 * - Any other string is treated as an annotation key lookup on WaypointAttributes.annotations
 */
export type TrackColorMode = BuiltInColorMode | (string & {});

export const BUILT_IN_COLOR_MODES: BuiltInColorMode[] = [
  'track',
  'sog',
  'cog',
  'nav_status',
  'name',
  'mmsi',
];

export const TRACK_COLOR_MODE_LABELS: Record<BuiltInColorMode, string> = {
  track: 'Track Color',
  sog: 'Speed (SOG)',
  cog: 'Course (COG)',
  nav_status: 'Nav Status',
  name: 'Vessel Name',
  mmsi: 'MMSI',
};

/** Check whether a color mode is a built-in (gradient/fixed) or an annotation key. */
export function isAnnotationColorMode(mode: TrackColorMode): boolean {
  return !(BUILT_IN_COLOR_MODES as string[]).includes(mode);
}

/**
 * Compact labels for color modes, suitable for dense UI such as a layer-control
 * subtitle or a badge (e.g. "SOG", "COG", "Name").
 */
export const TRACK_COLOR_MODE_SHORT_LABELS: Record<BuiltInColorMode, string> = {
  track: 'Solid',
  sog: 'SOG',
  cog: 'COG',
  nav_status: 'Nav Status',
  name: 'Name',
  mmsi: 'MMSI',
};

/**
 * Short label for a color mode. Returns the compact built-in label, or a
 * title-cased version of an annotation key for non-built-in modes.
 */
export function getTrackColorModeShortLabel(mode: TrackColorMode): string {
  if ((BUILT_IN_COLOR_MODES as string[]).includes(mode)) {
    return TRACK_COLOR_MODE_SHORT_LABELS[mode as BuiltInColorMode];
  }
  return mode
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// --- SOG Color Scale ---

export const SOG_STOPS: { threshold: number; color: string; label: string }[] =
  [
    { threshold: 0, color: '#16a34a', label: '0 kn' },
    { threshold: 2, color: '#65a30d', label: '2 kn' },
    { threshold: 5, color: '#ca8a04', label: '5 kn' },
    { threshold: 10, color: '#ea580c', label: '10 kn' },
    { threshold: 15, color: '#dc2626', label: '15 kn' },
    { threshold: 25, color: '#991b1b', label: '25+ kn' },
  ];

// --- COG Color Scale ---

export const COG_STOPS: { threshold: number; color: string; label: string }[] =
  [
    { threshold: 0, color: '#ef4444', label: 'N (0°)' },
    { threshold: 45, color: '#f97316', label: 'NE (45°)' },
    { threshold: 90, color: '#eab308', label: 'E (90°)' },
    { threshold: 135, color: '#22c55e', label: 'SE (135°)' },
    { threshold: 180, color: '#14b8a6', label: 'S (180°)' },
    { threshold: 225, color: '#3b82f6', label: 'SW (225°)' },
    { threshold: 270, color: '#6366f1', label: 'W (270°)' },
    { threshold: 315, color: '#a855f7', label: 'NW (315°)' },
  ];

// --- Dynamic SOG Classification ---

/** Color ramp for graduated SOG scales (6 colors, slow → fast) */
const SOG_COLOR_RAMP = [
  '#16a34a', // green — slowest
  '#65a30d', // lime-green
  '#ca8a04', // yellow/amber
  '#ea580c', // orange
  '#dc2626', // red
  '#991b1b', // dark red — fastest
];

export interface SogStop {
  threshold: number;
  color: string;
  label: string;
}

/** Format a speed value as a human-readable label */
function formatSogLabel(value: number, isLast: boolean): string {
  const rounded = Math.round(value * 10) / 10;
  return isLast ? `${rounded}+ kn` : `${rounded} kn`;
}

/**
 * Build SOG colour stops from a list of threshold values.
 *
 * Thresholds are sorted ascending and zipped with the supplied colours
 * (falling back to {@link SOG_COLOR_RAMP} when no colours are given). Use this
 * to render a SOG legend/track colouring from user-defined buckets and/or a
 * chosen colour palette.
 */
export function buildSogStopsFromThresholds(
  thresholds: number[],
  colors?: string[],
): SogStop[] {
  const sorted = [...thresholds]
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b);

  if (sorted.length === 0) return SOG_STOPS;

  const palette = colors && colors.length > 0 ? colors : SOG_COLOR_RAMP;

  return sorted.map((threshold, i) => ({
    threshold,
    color: palette[i] ?? palette[palette.length - 1],
    label: formatSogLabel(threshold, i === sorted.length - 1),
  }));
}

/**
 * Compute dynamic SOG color stops from actual track data.
 *
 * Extracts all SOG values from the provided tracks, applies the chosen
 * classification method, and returns color stops matching the data distribution.
 */
export function computeDynamicSogStops(
  tracks: Track[],
  method: ClassificationMethod,
  numClasses = 6,
): SogStop[] {
  // Collect all SOG values from track waypoints
  const sogValues: number[] = [];
  for (const track of tracks) {
    for (const wp of track.waypoints) {
      if (Number.isFinite(wp.sog_kn)) {
        sogValues.push(wp.sog_kn);
      }
    }
  }

  if (sogValues.length < 2) return SOG_STOPS;

  const breaks = computeBreaks(sogValues, method, numClasses);
  if (breaks.length === 0) return SOG_STOPS;

  // Build stops: first class starts at data minimum
  const min = Math.min(...sogValues);
  const thresholds = [min, ...breaks];
  const colors = SOG_COLOR_RAMP.slice(0, thresholds.length);

  return thresholds.map((threshold, i) => ({
    threshold,
    color: colors[i] ?? colors[colors.length - 1],
    label: formatSogLabel(threshold, i === thresholds.length - 1),
  }));
}

/** Resolve a SOG value to a color using custom stops */
export function sogToColorWithStops(sog: number, stops: SogStop[]): string {
  for (let i = stops.length - 1; i >= 0; i--) {
    if (sog >= stops[i].threshold) return stops[i].color;
  }
  return stops[0]?.color ?? SOG_STOPS[0].color;
}

// --- Categorical Palette ---

export const CATEGORY_PALETTE = [
  '#3b82f6',
  '#ef4444',
  '#22c55e',
  '#f59e0b',
  '#a855f7',
  '#ec4899',
  '#14b8a6',
  '#f97316',
  '#6366f1',
  '#84cc16',
];

// --- Color Resolution Functions ---

/** Get color for a SOG value using the gradient scale */
export function sogToColor(sog: number): string {
  for (let i = SOG_STOPS.length - 1; i >= 0; i--) {
    if (sog >= SOG_STOPS[i].threshold) return SOG_STOPS[i].color;
  }
  return SOG_STOPS[0].color;
}

/** Get color for a COG value (0–360°) using the gradient scale */
export function cogToColor(cog: number): string {
  const normalized = ((cog % 360) + 360) % 360;
  for (let i = COG_STOPS.length - 1; i >= 0; i--) {
    if (normalized >= COG_STOPS[i].threshold) return COG_STOPS[i].color;
  }
  return COG_STOPS[0].color;
}

/** Look up a consistent color for a categorical value from a pre-built palette */
export function categoryColor(
  value: string,
  palette: Map<string, string>,
): string {
  return palette.get(value) ?? CATEGORY_PALETTE[0];
}

/** Get the display color for a track segment based on the color mode */
export function getSegmentColor(
  track: Track,
  wp: WaypointAttributes,
  colorBy: TrackColorMode,
  palette: Map<string, string>,
  customSogStops?: SogStop[],
): string {
  switch (colorBy) {
    case 'track':
      return track.color;
    case 'sog':
      return customSogStops
        ? sogToColorWithStops(wp.sog_kn, customSogStops)
        : sogToColor(wp.sog_kn);
    case 'cog':
      return cogToColor(wp.heading_deg ?? 0);
    case 'nav_status': {
      const val = wp.nav_status ?? 'Under way using engine';
      return categoryColor(val, palette);
    }
    case 'name': {
      const val = wp.name ?? track.source.name;
      return categoryColor(val, palette);
    }
    case 'mmsi': {
      const val = String(wp.mmsi ?? track.source.mmsi ?? 'Unknown');
      return categoryColor(val, palette);
    }
    default: {
      // Annotation key lookup
      const val = wp.annotations?.[colorBy] ?? 'unknown';
      return categoryColor(val, palette);
    }
  }
}

// --- Palette Building ---

/** Build the categorical palette from tracks for a given color mode */
export function buildCategoryPalette(
  tracks: Track[],
  colorBy: TrackColorMode,
): Map<string, string> {
  if (colorBy === 'track' || colorBy === 'sog' || colorBy === 'cog')
    return new Map();

  const palette = new Map<string, string>();
  for (const track of tracks) {
    for (const wp of track.waypoints) {
      let value: string;
      if (colorBy === 'nav_status') {
        value = wp.nav_status ?? 'Under way using engine';
      } else if (colorBy === 'name') {
        value = wp.name ?? track.source.name;
      } else if (colorBy === 'mmsi') {
        value = String(wp.mmsi ?? track.source.mmsi ?? 'Unknown');
      } else {
        // Annotation key lookup
        value = wp.annotations?.[colorBy] ?? 'unknown';
      }
      if (!palette.has(value)) {
        palette.set(
          value,
          CATEGORY_PALETTE[palette.size % CATEGORY_PALETTE.length],
        );
      }
    }
  }
  return palette;
}

// --- Legend ---

export interface LegendItem {
  color: string;
  label: string;
}

/** Build legend items for a given color mode */
export function buildLegendItems(
  mode: TrackColorMode,
  categoryPalette: Map<string, string>,
  customSogStops?: SogStop[],
): LegendItem[] {
  if (mode === 'sog') {
    const stops = customSogStops ?? SOG_STOPS;
    return stops.map((s) => ({ color: s.color, label: s.label }));
  }
  if (mode === 'cog') {
    return COG_STOPS.map((s) => ({ color: s.color, label: s.label }));
  }
  if (mode === 'track') return [];
  return Array.from(categoryPalette.entries()).map(([label, color]) => ({
    color,
    label: label || '(default)',
  }));
}

// --- Annotation Discovery ---

/** Discover all unique annotation keys present across tracks */
export function getAnnotationKeys(tracks: Track[]): string[] {
  const keys = new Set<string>();
  for (const track of tracks) {
    for (const wp of track.waypoints) {
      if (wp.annotations) {
        for (const key of Object.keys(wp.annotations)) {
          keys.add(key);
        }
      }
    }
  }
  return Array.from(keys);
}
